import { useEffect, useRef } from "react";
import axios from "axios";
import { requestFcmToken, onForegroundMessage } from "../lib/firebase";
import { registerNativePush, stopNativePush, nativePushPermission, isAndroidShell } from "../lib/native";
import { isNativeApp, nativePlatform } from "../lib/platform";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const TOKEN_KEY = "angiolini_fcm_token";
const OPT_OUT_KEY = "angiolini_push_opt_out";
const PRIMER_KEY = "angiolini_push_primer_shown";
export const PUSH_CHANGED_EVENT = "angiolini-push-changed";

const ls = {
  get: (k) => { try { return localStorage.getItem(k); } catch { return null; } },
  set: (k, v) => { try { localStorage.setItem(k, v); } catch { /* noop */ } },
  del: (k) => { try { localStorage.removeItem(k); } catch { /* noop */ } },
};

/** Клиент сам выключил push в профиле — повторно не включаем. */
export const isPushOptedOut = () => ls.get(OPT_OUT_KEY) === "1";
/** Объяснение перед системным запросом показываем один раз на устройство. */
export const wasPushPrimerShown = () => ls.get(PRIMER_KEY) === "1";
export const markPushPrimerShown = () => ls.set(PRIMER_KEY, "1");
const notifyPushChanged = () => {
  try { window.dispatchEvent(new Event(PUSH_CHANGED_EVENT)); } catch { /* noop */ }
};

// Тихая регистрация при запуске нужна один раз на сессию пользователя,
// даже если хук смонтирован в нескольких местах (layout + профиль).
let bootstrappedFor = null;

/**
 * Hook that, when the user is authenticated, requests an FCM token,
 * sends it to the backend, and listens for foreground messages.
 *
 * Возвращает { enable, disable, status } — состояние подписки конкретного
 * устройства, чтобы профиль мог показать честный переключатель.
 */
export function useFCM(user) {
  const registered = useRef(false);

  useEffect(() => {
    if (!user) return;
    if (bootstrappedFor === user.id) return;
    bootstrappedFor = user.id;
    // Пользователь сам выключил push на этом устройстве — не включаем молча
    const optedOut = ls.get(OPT_OUT_KEY) === "1";
    if (isNativeApp()) {
      // Android 12 и ниже (и любой случай, когда разрешение уже есть):
      // подписка включается сама, без диалогов и действий клиента.
      if (!optedOut) registerNativePush(sendToken, { requestIfNeeded: false });
      return;
    }
    // Only try silent enable if permission was already granted before
    if (!optedOut && typeof Notification !== "undefined" && Notification.permission === "granted") {
      enableInternal();
    }
    // Always set up foreground listener (no permission required for the listener itself)
    let unsubscribe = () => {};
    (async () => {
      unsubscribe = await onForegroundMessage((payload) => {
        const n = payload?.notification || {};
        const title = n.title || "Angiolini VIP";
        const body = n.body || "";
        // Lightweight visual toast (use native Notification if allowed, else console)
        try {
          if (Notification.permission === "granted") {
            new Notification(title, { body, icon: "/icon-192.png" });
          }
        } catch { /* noop */ }
        // eslint-disable-next-line no-console
        console.log("[FCM foreground]", title, body, payload?.data);
      });
    })();
    return () => { try { unsubscribe(); } catch { /* noop */ } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  async function sendToken(token) {
    if (!token) return false;
    try {
      await axios.post(`${API}/notifications/register`,
        { token, platform: isNativeApp() ? nativePlatform() : "web" },
        { withCredentials: true });
      registered.current = true;
      ls.set(TOKEN_KEY, token);
      ls.del(OPT_OUT_KEY);
      notifyPushChanged();
      return true;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("FCM register failed", e);
      return false;
    }
  }

  async function enableInternal() {
    // В Android/iOS-сборке токен выдаёт нативный FCM, а не service worker
    if (isNativeApp()) {
      const state = await registerNativePush(sendToken);
      return state === "granted" ? true : state;      // "denied" | "prompt" | "error" | "web"
    }
    if (registered.current && ls.get(TOKEN_KEY)) return true;
    const token = await requestFcmToken();
    return sendToken(token);
  }

  /** Отписка только этого устройства: токен удаляется у пользователя на сервере. */
  async function disableInternal() {
    const token = ls.get(TOKEN_KEY);
    if (token) {
      try {
        await axios.post(`${API}/notifications/unregister`, { token }, { withCredentials: true });
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("FCM unregister failed", e);
        return false;
      }
    }
    ls.del(TOKEN_KEY);
    ls.set(OPT_OUT_KEY, "1");
    registered.current = false;
    await stopNativePush();
    notifyPushChanged();
    return true;
  }

  /** Фактическое состояние на этом устройстве: on | off | blocked | bridge | unsupported. */
  async function statusInternal() {
    const optedOut = ls.get(OPT_OUT_KEY) === "1";
    if (isNativeApp()) {
      if (nativePlatform() !== "android") return "unsupported";
      const perm = await nativePushPermission();
      if (perm === "denied") return "blocked";
      if (perm !== "granted") return "off";
      return optedOut || !ls.get(TOKEN_KEY) ? "off" : "on";
    }
    if (typeof Notification === "undefined") {
      // Внутри Android-оболочки это значит, что мост Capacitor не поднялся —
      // не "устройство не поддерживает", а сбой сборки/инициализации.
      return isAndroidShell() ? "bridge" : "unsupported";
    }
    if (Notification.permission === "denied") return "blocked";
    if (Notification.permission !== "granted") return "off";
    return optedOut ? "off" : "on";
  }

  return { enable: enableInternal, disable: disableInternal, status: statusInternal };
}
